/**
 * BSGOTECH — Brand Cards grid.
 *
 * Uniform premium corporate grid presenting the four entities of the ecosystem:
 *   BSGOTECH · NeuNexus · RENALIX · NeuGreen Energy
 *
 * Every card has the exact same:
 *   - logo tile size (white 96×64 rounded frame, 44px max-height logo)
 *   - vertical rhythm and padding
 *   - typography scale (label / name / role / one-liner)
 *   - hover interaction (subtle lift + accent border glow)
 *
 * Additive: does NOT remove or modify any existing section.
 * Fully DE / EN / PT via LangCtx.
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { LangCtx } from "@/i18n/neunexus";

const P = {
  bg:      "#080B12",
  bg2:     "#0A0F1A",
  surface: "rgba(255,255,255,0.03)",
  border:  "rgba(255,255,255,0.08)",
  ink:     "#E8ECF0",
  dim:     "rgba(232,236,240,0.68)",
  silver:  "#C0C7CE",
  white:   "#FFFFFF",
  green:   "#22C55E",
  teal:    "#14B8A6",
  rose:    "#F43F5E",
  blue:    "#3B82F6",
};

const DATA = {
  en: {
    eyebrow: "Brand Architecture",
    title: "The ecosystem, at a glance.",
    subtitle: "One parent company, one integration platform, two operational divisions — presented with the same corporate weight and clarity.",
    cards: [
      { id: "bsgotech", label: "Technology Company",            name: "BSGOTECH",        role: "AI · Cloud · Data · Innovation",   line: "Building intelligent digital ecosystems.",             logo: "/bsgotech/logo-official.png",        href: "/our-ecosystem",   accent: "white"  },
      { id: "neunexus", label: "Industrial Innovation Platform", name: "NeuNexus",        role: "Integration · Partners · Industry", line: "Connecting technologies, partners and industries.",   logo: "/neunexus/logo-official.png",       href: "/neunexus",        accent: "green"  },
      { id: "renalix",  label: "AI-Powered Healthcare",          name: "RENALIX",         role: "Predictive Renal Intelligence",     line: "Predictive kidney healthcare powered by AI.",         logo: "/renalix/logo-official.png",        href: "/renalix",         accent: "rose"   },
      { id: "neugreen", label: "Renewable Energy",               name: "NeuGreen Energy", role: "Solar · Storage · Smart Energy",    line: "Solar, storage and intelligent energy infrastructure.", logo: "/neugreen/logo-official.png",       href: "/neugreen",        accent: "teal"   },
    ],
    cta: "Enter",
  },
  de: {
    eyebrow: "Markenarchitektur",
    title: "Das Ökosystem auf einen Blick.",
    subtitle: "Eine Muttergesellschaft, eine Integrationsplattform, zwei operative Divisionen — mit gleichem unternehmerischen Gewicht und Klarheit präsentiert.",
    cards: [
      { id: "bsgotech", label: "Technologieunternehmen",          name: "BSGOTECH",        role: "KI · Cloud · Daten · Innovation",       line: "Wir bauen intelligente digitale Ökosysteme.",             logo: "/bsgotech/logo-official.png",       href: "/our-ecosystem", accent: "white"  },
      { id: "neunexus", label: "Industrielle Innovationsplattform", name: "NeuNexus",       role: "Integration · Partner · Industrie",     line: "Wir verbinden Technologien, Partner und Industrien.",     logo: "/neunexus/logo-official.png",       href: "/neunexus", accent: "green"  },
      { id: "renalix",  label: "KI-gestützte Gesundheit",         name: "RENALIX",         role: "Prädiktive Nephrologie",                line: "Prädiktive Nierenversorgung mit KI.",                     logo: "/renalix/logo-official.png",        href: "/renalix",  accent: "rose"   },
      { id: "neugreen", label: "Erneuerbare Energie",             name: "NeuGreen Energy", role: "Solar · Speicher · Smart Energy",       line: "Solar, Speicher und intelligente Energieinfrastruktur.",  logo: "/neugreen/logo-official.png",       href: "/neugreen", accent: "teal"   },
    ],
    cta: "Öffnen",
  },
  pt: {
    eyebrow: "Arquitectura de Marca",
    title: "O ecossistema num só olhar.",
    subtitle: "Uma empresa-mãe, uma plataforma de integração, duas divisões operacionais — apresentadas com o mesmo peso e clareza corporativa.",
    cards: [
      { id: "bsgotech", label: "Empresa Tecnológica",            name: "BSGOTECH",        role: "IA · Cloud · Dados · Inovação",     line: "Construir ecossistemas digitais inteligentes.",         logo: "/bsgotech/logo-official.png",       href: "/our-ecosystem", accent: "white"  },
      { id: "neunexus", label: "Plataforma de Inovação Industrial", name: "NeuNexus",     role: "Integração · Parceiros · Indústria", line: "A ligar tecnologias, parceiros e indústrias.",          logo: "/neunexus/logo-official.png",       href: "/neunexus", accent: "green"  },
      { id: "renalix",  label: "Saúde com IA",                    name: "RENALIX",        role: "Inteligência Renal Preditiva",       line: "Cuidado renal preditivo potenciado por IA.",            logo: "/renalix/logo-official.png",        href: "/renalix",  accent: "rose"   },
      { id: "neugreen", label: "Energia Renovável",              name: "NeuGreen Energy", role: "Solar · Armazenamento · Smart Energy", line: "Solar, armazenamento e infraestrutura energética inteligente.", logo: "/neugreen/logo-official.png",   href: "/neugreen", accent: "teal"   },
    ],
    cta: "Entrar",
  },
};

const ACCENT = { white: P.white, green: P.green, rose: P.rose, teal: P.teal };

const useB = () => {
  const { lang } = React.useContext(LangCtx);
  return DATA[lang] || DATA.en;
};

const BrandCard = ({ card, cta, idx }) => {
  const c = ACCENT[card.accent] || P.silver;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ delay: idx * 0.08, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -4 }}
      className="group relative flex flex-col h-full"
    >
      <Link
        to={card.href}
        data-testid={`brand-card-${card.id}`}
        className="flex flex-col h-full rounded-xl overflow-hidden transition-all duration-300"
        style={{
          background: `linear-gradient(180deg, ${P.bg2} 0%, ${P.bg} 100%)`,
          border: `1px solid ${P.border}`,
        }}
      >
        {/* accent top rule (thin) */}
        <div style={{ height: 2, background: `linear-gradient(90deg, ${c}66 0%, ${c}00 100%)` }} aria-hidden="true" />

        {/* Uniform logo tile — same dimensions across all four cards */}
        <div className="px-7 pt-7">
          <div
            className="flex items-center justify-center transition group-hover:brightness-110"
            style={{
              height: 84,
              borderRadius: 10,
              background: "rgba(255,255,255,0.97)",
              border: `1px solid ${c}33`,
              boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.4)`,
            }}
          >
            <img
              src={card.logo}
              alt={`${card.name} logo`}
              className="object-contain select-none"
              style={{ maxHeight: 44, maxWidth: 160 }}
              draggable="false"
              loading="lazy"
            />
          </div>
        </div>

        {/* Uniform text block */}
        <div className="px-7 pt-6 pb-7 flex-1 flex flex-col">
          <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: c }}>
            {card.label}
          </div>
          <div className="mt-2 font-semibold tracking-tight text-[20px]" style={{ color: "#fff", letterSpacing: "-0.01em" }}>
            {card.name}
          </div>
          <div className="mt-1 text-[11.5px] uppercase tracking-[0.16em]" style={{ color: P.silver }}>
            {card.role}
          </div>
          <p className="mt-4 text-[13px] leading-relaxed flex-1" style={{ color: P.dim }}>
            {card.line}
          </p>

          <div className="mt-6 inline-flex items-center gap-1.5 text-[11.5px] font-semibold transition"
            style={{ color: c === P.white ? P.ink : c }}>
            <span className="uppercase tracking-[0.2em]">{cta}</span>
            <ArrowUpRight size={13} className="transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </div>
        </div>

        {/* faint glow on hover */}
        <div className="pointer-events-none absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ boxShadow: `0 0 0 1px ${c}55, 0 20px 60px -20px ${c}44` }} aria-hidden="true" />
      </Link>
    </motion.div>
  );
};

export default function BrandCards() {
  const t = useB();
  return (
    <section
      id="brand-cards"
      data-testid="brand-cards"
      className="relative border-t"
      style={{ background: P.bg, borderColor: P.border }}
    >
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{
        backgroundImage: `radial-gradient(circle at 50% -10%, ${P.blue}12 0%, transparent 55%)`,
      }} />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl"
        >
          <div className="text-[10px] uppercase tracking-[0.42em] font-semibold" style={{ color: P.green }}>
            {t.eyebrow}
          </div>
          <h2 className="mt-4 font-semibold tracking-tight text-[26px] sm:text-[34px] lg:text-[42px] leading-[1.05]"
            style={{ color: "#fff", letterSpacing: "-0.02em" }}>
            {t.title}
          </h2>
          <p className="mt-4 max-w-2xl text-[13.5px] leading-relaxed" style={{ color: P.dim }}>
            {t.subtitle}
          </p>
        </motion.div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
          {t.cards.map((card, i) => (
            <BrandCard key={card.id} card={card} cta={t.cta} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
